import tableColumnSortHelper, { SortedColumn } from 'helpers/table-column-sort-helper';
import useTranslation from 'modules/localization';
import React from 'react';
import { connect } from 'react-redux';
import { orderService } from 'services';
import { Cmd, EntryType } from 'shared/trade/enums';
import { RootState } from 'store/types';
import { OrderHistoryPeriods } from './constants';
import orderHistorHelper from './helper';
import OrderHistoryTableToolBox from './toolbox';
import { OrderHistoryTableProps } from './types';
import { DealDto } from '../../../api/service-proxies';

const HistoryTable = (props: OrderHistoryTableProps) => {
    const [sortedColumn, setSortedColumn] = React.useState<SortedColumn>(null);
    const [period, setPeriod] = React.useState(OrderHistoryPeriods.TODAY);
    const [startDate, setStartDate] = React.useState<any>('');
    const [endDate, setEndDate] = React.useState<any>('');
    const { t } = useTranslation();

    // <ColumnSortFeature>
    const deals: DealDto[] = tableColumnSortHelper.getSortedData({ sortStateGetter: sortedColumn, data: props.deals || [] });

    const handleColumnClick = (key: string, type: 'number' | 'string') => tableColumnSortHelper.changeSortedColumn({ 
        columnKey: key, 
        columnType: type, 
        sortStateGetter: sortedColumn, 
        sortStateSetter: setSortedColumn 
    });

    const renderColumnSortIcon = (key: string) => tableColumnSortHelper.renderSortedColumnIconInput({
        columnKey: key,
        sortStateGetter: sortedColumn
    });
        
    // <Calculations>
    const getTotal = (orderKey: keyof DealDto) => {
        let calculation = 0;

        if (deals) {
            deals.forEach((deal) => calculation += deal[orderKey] as number);
        }

        return calculation.toFixed(2);
    }
    // </Calculations>

    React.useEffect(() => {
        if (startDate?.value && endDate?.value) {
            orderService.provider.getHistory(startDate.value, endDate.value);
        }
    }, [startDate, endDate])

    React.useEffect(() => {
        const date = orderHistorHelper.getPeriodDate(period);
        if (date) {
            setStartDate(date.startDate);
            setEndDate(date.endDate);
        } 
    }, [period])

    return (
        <div className='order-history-table'>
            <OrderHistoryTableToolBox usePeriod={[period, setPeriod]} useStartDate={[startDate, setStartDate]} useEndDate={[endDate, setEndDate]} />
            <table>
                <thead>
                    <tr>
                        <th style={{ width: '40px' }} onClick={() => handleColumnClick('time', 'number')}>{t('Time')} {renderColumnSortIcon('time')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('symbol', 'string')}>{t('Symbol')} {renderColumnSortIcon('symbol')}</th>
                        <th style={{ width: '25px' }} onClick={() => handleColumnClick('entry', 'number')}>{t('Entry')} {renderColumnSortIcon('entry')}</th>
                        <th style={{ width: '25px' }} onClick={() => handleColumnClick('order', 'number')}>{t('Order')} {renderColumnSortIcon('order')}</th>
                        <th style={{ width: '25px' }} onClick={() => handleColumnClick('deal', 'number')}>{t('Deal')} {renderColumnSortIcon('deal')}</th>
                        <th style={{ width: '15px' }} onClick={() => handleColumnClick('action', 'number')}>{t('Action')} {renderColumnSortIcon('action')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('volume', 'number')}>{t('Volume')} {renderColumnSortIcon('volume')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('price', 'number')}>{t('Price')} {renderColumnSortIcon('price')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('sl', 'number')}>{t('StopLoss')} {renderColumnSortIcon('sl')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('tp', 'number')}>{t('TakeProfit')} {renderColumnSortIcon('tp')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('swap', 'number')}>{t('Swap')} {renderColumnSortIcon('swap')}</th>
                        <th style={{ width: '20px' }} onClick={() => handleColumnClick('profit', 'number')}>{t('ProfitAndLoss')} {renderColumnSortIcon('profit')}</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        deals?.map((history) => {
                            const digit = history.digits;

                            const deal = '#' + history.deal;
                            const order = '#' + history.order;
                            const action = Cmd[history.action];
                            const volume = history.volume.toFixed(2);
                            const symbol = history.symbol;

                            const time = history.time.setZone(props.tradingViewTimeZone).toFormat('yyyy.LL.dd HH:mm:ss');
                            const price = history.price.toFixed(digit);
                            const stopLoss = history.priceSL === 0 ? "" : history.priceSL.toFixed(digit);
                            const takeProfit = history.priceTP === 0 ? "" : history.priceTP.toFixed(digit);
                            const swap = history.swap.toFixed(2);
                            const profit = history.profit.toFixed(2);
                            const entry = EntryType[history.entry];

                            return (
                                <tr key={history.deal}>
                                    <td>{time}</td>
                                    <td>{symbol}</td>
                                    <td>{entry}</td>
                                    <td>{order}</td>
                                    <td>{deal}</td>
                                    <td>{action}</td>
                                    <td>{volume}</td>
                                    <td>{price}</td>
                                    <td>{stopLoss}</td>
                                    <td>{takeProfit}</td>
                                    <td>{swap}</td>
                                    <td>{profit}</td>
                                </tr>
                            )
                        })
                    }

                    <tr>
                        <td colSpan={9}></td>
                        <td>
                            <strong>{t('TotalOrders')}</strong>
                        </td>
                        <td>
                            <strong>{getTotal('swap')}</strong>
                        </td>
                        <td>
                            <strong>{getTotal('profit')}</strong>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    deals: state.orderPositionDeal.deals,
    tradingViewTimeZone: state.account.tradingViewTimeZone
})

export default connect(mapStateToProps)(HistoryTable);